import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Seo from '../../components/Seo';
import RichTextEditor from '../../components/RichTextEditor';
import { Field, Spinner, PageLoader } from '../../components/ui';
import { newsApi, mediaApi } from '../../api/resources';
import { assetUrl } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import GalleryEditor from '../../components/GalleryEditor';
import { galleryItemsFromRecord, buildGalleryPayload, applyGalleryErrors } from '../../lib/gallery';

const EMPTY = {
  title: '',
  excerpt: '',
  body: '',
  featured_image_path: '',
  gallery: [],
};

export default function NewsForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [galleryDirty, setGalleryDirty] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    let active = true;
    newsApi
      .get(id)
      .then((n) => {
        if (!active) return;
        setForm({
          title: n.title || '',
          excerpt: n.excerpt || '',
          body: n.body || '',
          featured_image_path: n.featured_image_path || '',
          gallery: galleryItemsFromRecord(n),
        });
      })
      .catch(() => toast.error('Could not load this news item.'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  /** Mark the gallery dirty and apply a functional update from GalleryEditor. */
  function setGallery(updater) {
    setForm((f) => ({ ...f, gallery: updater(f.gallery) }));
    setGalleryDirty(true);
  }

  async function handleImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', 'image');
      fd.append('alt_text', file.name);
      const media = await mediaApi.upload(fd);
      const value = media?.url || media?.path || media?.file_path || '';
      if (!value) throw new Error('Upload succeeded but no URL was returned.');
      set('featured_image_path', value);
      toast.success('Image uploaded.');
    } catch (err) {
      toast.error(err.message || 'Upload failed. You can paste an image path instead.');
    } finally {
      setUploading(false);
    }
  }

  async function save(submitAfter) {
    // Resolve the gallery payload before the request so a broken media
    // reference surfaces a clear message rather than a 422.
    let galleryPayload;
    if (galleryDirty) {
      try {
        galleryPayload = buildGalleryPayload(form.gallery); // may be [] to clear
      } catch (err) {
        toast.error(err.message);
        return;
      }
    }

    setErrors({});
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        excerpt: form.excerpt || null,
        body: form.body,
        featured_image_path: form.featured_image_path || null,
      };
      if (galleryDirty) payload.gallery = galleryPayload;
      const saved = isEdit ? await newsApi.update(id, payload) : await newsApi.create(payload);
      if (submitAfter && saved?.id) {
        await newsApi.submit(saved.id, 'Submitted for review.');
        toast.success('News saved and submitted for review.');
      } else {
        toast.success(isEdit ? 'News updated.' : 'Draft saved.');
      }
      navigate('/member/news');
    } catch (err) {
      let handled = false;
      if (err.status === 422 && galleryDirty) {
        const { items, matched } = applyGalleryErrors(form.gallery, err.errors);
        if (matched) {
          setForm((f) => ({ ...f, gallery: items }));
          toast.error('Please fix the highlighted gallery items.');
          handled = true;
        }
      }
      if (!handled) {
        setErrors(err.errors || {});
        toast.error(err.message || 'Could not save news.');
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <PageLoader />;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Seo title={isEdit ? 'Edit News' : 'Submit News'} />
      <h1 className="font-display text-2xl font-bold">{isEdit ? 'Edit News' : 'Submit News'}</h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save(false);
        }}
        className="card space-y-5 p-6"
      >
        <Field label="Title" error={errors.title?.[0]}>
          <input className="input" value={form.title} onChange={(e) => set('title', e.target.value)} required />
        </Field>

        <Field label="Excerpt" hint="A short summary shown on cards and in search results." error={errors.excerpt?.[0]}>
          <textarea
            className="input"
            rows={2}
            value={form.excerpt}
            onChange={(e) => set('excerpt', e.target.value)}
          />
        </Field>

        <Field label="Body" hint="Format text, add links, insert images, and build tables." error={errors.body?.[0]}>
          <RichTextEditor
            id="news-body"
            value={form.body}
            onChange={(html) => set('body', html)}
            placeholder="Write your news update…"
          />
        </Field>

        <Field
          label="Featured image"
          error={errors.featured_image_path?.[0]}
          hint="Upload an image or paste an existing path."
        >
          <div className="flex flex-col gap-2 sm:flex-row">
            <input type="file" accept="image/*" onChange={handleImage} className="text-sm" />
            <input
              className="input"
              placeholder="news/my-image.jpg"
              value={form.featured_image_path}
              onChange={(e) => set('featured_image_path', e.target.value)}
            />
          </div>
          {uploading && <p className="mt-1 text-xs text-ink-muted">Uploading…</p>}
          {form.featured_image_path && (
            <img
              src={assetUrl(form.featured_image_path)}
              alt="Featured preview"
              className="mt-3 aspect-video w-full max-w-sm rounded-lg object-cover shadow-card"
            />
          )}
        </Field>

        <GalleryEditor
          items={form.gallery}
          onChange={setGallery}
          disabled={saving}
          onUploadingChange={setGalleryUploading}
        />

        <div className="flex flex-wrap gap-3 border-t border-cream-300 pt-5">
          <button type="submit" className="btn-outline" disabled={saving || galleryUploading}>
            {saving ? <Spinner className="h-4 w-4" /> : 'Save Draft'}
          </button>
          <button
            type="button"
            onClick={() => save(true)}
            className="btn-primary"
            disabled={saving || galleryUploading}
          >
            Save &amp; Submit for Review
          </button>
          <button type="button" onClick={() => navigate('/member/news')} className="btn-ghost ml-auto">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
